import React, { useMemo } from 'react'

import { F0Box, F0TagBalance, F0Text } from '@factorialco/f0-react'
import { Widget } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

const formatDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) return '0m'
  if (seconds < 3600) {
    const mins = Math.round(seconds / 60)
    return `${mins}m`
  }
  const hours = Math.floor(seconds / 3600)
  const mins = Math.round((seconds % 3600) / 60)
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
}

type KpiCardProps = {
  title: string
  value: number
  previous?: number | null
  isAllTime: boolean
  suffix?: string
  formatValue?: (v: number) => string
}

const KpiCard: React.FC<KpiCardProps> = ({
  title,
  value,
  previous,
  isAllTime,
  suffix,
  formatValue,
}) => {
  const displayValue = useMemo(() => {
    if (formatValue) return formatValue(value)
    if (suffix) return `${value}${suffix}`
    return `${value}`
  }, [formatValue, value, suffix])

  const hasPrevious = !isAllTime && previous != null && previous !== 0
  const percentage = hasPrevious ? Math.round(((value - previous) / previous) * 100) : 0
  const showComparison = hasPrevious && percentage !== 0

  return (
    <F0Box grow minWidth="44">
      <Widget header={{ title }}>
        <F0Box display="flex" flexDirection="row" alignItems="center" gap="sm">
          <F0Text variant="label" content={displayValue} />
          {showComparison && <F0TagBalance percentage={percentage} amount={null} />}
        </F0Box>
      </Widget>
    </F0Box>
  )
}

type InsightsKpiCardsProps = {
  assignedCount: number
  assignedCountPrevious?: number | null
  resolvedCount: number
  resolvedCountPrevious?: number | null
  inProgressCount: number
  inProgressCountPrevious?: number | null
  averageResolutionTimeSeconds?: number | null
  averageResolutionTimeSecondsPrevious?: number | null
  slaCompliancePercent?: number | null
  slaCompliancePercentPrevious?: number | null
  isAllTime: boolean
}

export const InsightsKpiCards: React.FC<InsightsKpiCardsProps> = ({
  assignedCount,
  assignedCountPrevious,
  resolvedCount,
  resolvedCountPrevious,
  inProgressCount,
  inProgressCountPrevious,
  averageResolutionTimeSeconds,
  averageResolutionTimeSecondsPrevious,
  slaCompliancePercent,
  slaCompliancePercentPrevious,
  isAllTime,
}) => {
  return (
    <F0Box display="flex" flexDirection="row" flexWrap="wrap" gap="md">
      <KpiCard
        title={i18n.t('ticketing.inbox.insights.kpi.assigned')}
        value={assignedCount}
        previous={assignedCountPrevious}
        isAllTime={isAllTime}
      />
      <KpiCard
        title={i18n.t('ticketing.inbox.insights.kpi.resolved')}
        value={resolvedCount}
        previous={resolvedCountPrevious}
        isAllTime={isAllTime}
      />
      <KpiCard
        title={i18n.t('ticketing.inbox.insights.kpi.in_progress')}
        value={inProgressCount}
        previous={inProgressCountPrevious}
        isAllTime={isAllTime}
      />
      <KpiCard
        title={i18n.t('ticketing.inbox.insights.kpi.avg_resolution_time')}
        value={averageResolutionTimeSeconds ?? 0}
        previous={averageResolutionTimeSecondsPrevious}
        isAllTime={isAllTime}
        formatValue={formatDuration}
      />
      <KpiCard
        title={i18n.t('ticketing.inbox.insights.kpi.sla_compliance')}
        value={slaCompliancePercent ?? 0}
        previous={slaCompliancePercentPrevious}
        isAllTime={isAllTime}
        suffix="%"
      />
    </F0Box>
  )
}
